import * as THREE from 'three';
import { Tank } from './Tank';
import {
  COLOR_ENEMY_BODY,
  COLOR_ENEMY_TURRET,
  ENEMY_BASE_HP,
  ENEMY_BASE_SPEED,
  ENEMY_BASE_FIRE_RATE,
  ENEMY_DETECT_RANGE,
  TURRET_TURN_SPEED,
  ARENA_HALF,
  TANK_BODY_WIDTH,
  TANK_BODY_DEPTH,
} from '../config';
import { Bullet } from './Bullet';

const DEG_TO_RAD = Math.PI / 180;

enum EnemyState { Patrol, Chase, Attack }

export class EnemyTank extends Tank {
  fireTimer = 0;
  private state: EnemyState = EnemyState.Patrol;
  private patrolTarget = new THREE.Vector3();
  private patrolTimer = 0;
  private changePatrolInterval: number;

  constructor(waveNumber: number) {
    const hp = ENEMY_BASE_HP + ENEMY_BASE_HP * 0.2 * (waveNumber - 1);
    const speed = ENEMY_BASE_SPEED + ENEMY_BASE_SPEED * 0.15 * (waveNumber - 1);
    super(COLOR_ENEMY_BODY, COLOR_ENEMY_TURRET, hp, speed);
    this.fireTimer = Math.random() * ENEMY_BASE_FIRE_RATE;
    this.changePatrolInterval = 2 + Math.random() * 3;
    this.pickNewPatrolTarget();
  }

  spawnAt(x: number, z: number): void {
    this.group.position.set(x, 0, z);
    this.alive = true;
    this.hp = this.maxHp;
    this.state = EnemyState.Patrol;
    this.fireTimer = Math.random() * ENEMY_BASE_FIRE_RATE;
    this.recoilAmount = 0;
    this.barrelGroup.position.z = 0;
    const mat = this.bodyMesh.material as THREE.MeshStandardMaterial;
    mat.opacity = 1;
    mat.transparent = false;
  }

  update(delta: number, playerPos: THREE.Vector3, bullets: Bullet[], obstacles: THREE.Box3[]): void {
    if (!this.alive) return;

    this.fireTimer = Math.max(0, this.fireTimer - delta);
    this.updateRecoil(delta);

    const distToPlayer = this.group.position.distanceTo(playerPos);
    const fireRate = ENEMY_BASE_FIRE_RATE;

    if (distToPlayer < ENEMY_DETECT_RANGE) {
      this.state = distToPlayer < ENEMY_DETECT_RANGE * 0.5 ? EnemyState.Attack : EnemyState.Chase;
    } else {
      this.state = EnemyState.Patrol;
    }

    let speedRatio = 0;
    switch (this.state) {
      case EnemyState.Patrol:
        speedRatio = this.updatePatrol(delta);
        break;
      case EnemyState.Chase:
        speedRatio = this.updateChase(delta, playerPos);
        break;
      case EnemyState.Attack:
        speedRatio = this.updateAttack(delta, playerPos, bullets, fireRate);
        break;
    }

    this.updateWheels(delta, speedRatio);
    this.clampToArena();
    this.avoidObstacles(delta, obstacles);
  }

  private updatePatrol(delta: number): number {
    this.patrolTimer += delta;
    if (this.patrolTimer >= this.changePatrolInterval) {
      this.pickNewPatrolTarget();
      this.patrolTimer = 0;
    }
    this.turretGroup.rotation.y += 0.5 * DEG_TO_RAD * delta;
    return this.moveToward(this.patrolTarget, delta);
  }

  private updateChase(delta: number, playerPos: THREE.Vector3): number {
    const dx = playerPos.x - this.group.position.x;
    const dz = playerPos.z - this.group.position.z;
    const angle = Math.atan2(dx, dz);
    const targetAngle = this.angleDifference(angle, this.group.rotation.y);
    this.group.rotation.y += targetAngle * 2 * DEG_TO_RAD * delta;
    return this.moveToward(playerPos, delta);
  }

  private updateAttack(delta: number, playerPos: THREE.Vector3, bullets: Bullet[], fireRate: number): number {
    const dir = new THREE.Vector3().subVectors(this.group.position, playerPos);
    const dist = dir.length();
    dir.normalize();

    if (dist > 8) {
      this.group.position.addScaledVector(dir, -this.moveSpeed * 0.5 * delta);
    } else if (dist < 5) {
      this.group.position.addScaledVector(dir, this.moveSpeed * 0.3 * delta);
    }

    const targetAngle = Math.atan2(-dir.x, -dir.z);
    const angleDiff = this.angleDifference(targetAngle, this.group.rotation.y);
    this.group.rotation.y += angleDiff * 3 * DEG_TO_RAD * delta;

    const turretTarget = Math.atan2(playerPos.x - this.group.position.x, playerPos.z - this.group.position.z);
    const turretDiff = this.angleDifference(turretTarget - this.group.rotation.y, this.turretGroup.rotation.y);
    this.turretGroup.rotation.y += turretDiff * (TURRET_TURN_SPEED * 0.6) * DEG_TO_RAD * delta;

    if (this.fireTimer <= 0 && Math.abs(turretDiff) < 0.3) {
      const muzzlePos = new THREE.Vector3();
      const barrelDir = new THREE.Vector3();
      this.getMuzzleWorldPos(muzzlePos);
      this.getBarrelDirection(barrelDir);
      bullets.push(new Bullet(muzzlePos, barrelDir, false));
      this.fireTimer = fireRate;
      this.triggerRecoil();
    }

    return dist > 8 ? 0.5 : (dist < 5 ? -0.3 : 0);
  }

  private moveToward(target: THREE.Vector3, delta: number): number {
    const dx = target.x - this.group.position.x;
    const dz = target.z - this.group.position.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    if (dist < 0.5) return 0;
    const moveX = (dx / dist) * this.moveSpeed * 0.5 * delta;
    const moveZ = (dz / dist) * this.moveSpeed * 0.5 * delta;
    this.group.position.x += moveX;
    this.group.position.z += moveZ;
    const targetAngle = Math.atan2(dx, dz);
    const diff = this.angleDifference(targetAngle, this.group.rotation.y);
    this.group.rotation.y += diff * 2 * DEG_TO_RAD * delta;
    return 0.5;
  }

  private avoidObstacles(delta: number, obstacles: THREE.Box3[]): void {
    const tankBox = this.getBoundingBox();
    for (const obs of obstacles) {
      if (tankBox.intersectsBox(obs)) {
        const center = new THREE.Vector3();
        obs.getCenter(center);
        const pushDir = new THREE.Vector3().subVectors(this.group.position, center);
        pushDir.y = 0;
        if (pushDir.length() < 0.01) pushDir.set(Math.random() - 0.5, 0, Math.random() - 0.5);
        pushDir.normalize();
        this.group.position.addScaledVector(pushDir, this.moveSpeed * delta);
        break;
      }
    }
  }

  private pickNewPatrolTarget(): void {
    const margin = 3;
    this.patrolTarget.set(
      (Math.random() - 0.5) * (ARENA_HALF * 2 - margin * 2),
      0,
      (Math.random() - 0.5) * (ARENA_HALF * 2 - margin * 2),
    );
  }

  private angleDifference(target: number, current: number): number {
    let diff = target - current;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    return diff;
  }

  private clampToArena(): void {
    const halfW = TANK_BODY_WIDTH / 2;
    const halfD = TANK_BODY_DEPTH / 2;
    this.group.position.x = THREE.MathUtils.clamp(this.group.position.x, -ARENA_HALF + halfW + 1, ARENA_HALF - halfW - 1);
    this.group.position.z = THREE.MathUtils.clamp(this.group.position.z, -ARENA_HALF + halfD + 1, ARENA_HALF - halfD - 1);
  }

  getBoundingBox(): THREE.Box3 {
    return new THREE.Box3().setFromObject(this.group);
  }

  takeDamage(damage: number): boolean {
    if (!this.alive) return false;
    this.hp -= damage;
    const mat = this.bodyMesh.material as THREE.MeshStandardMaterial;
    mat.transparent = true;
    mat.opacity = 0.5;
    setTimeout(() => { if (this.alive) mat.opacity = 1; }, 100);
    if (this.hp <= 0) { this.hp = 0; this.alive = false; return true; }
    return false;
  }
}
